import { Bookmark } from '../types/api';

export type SelectionPositionClass =
  | 'selected-single'
  | 'selected-top'
  | 'selected-middle'
  | 'selected-bottom';

/**
 * Whole-section batch: one contiguous selected column — first row `selected-top`,
 * last row `selected-bottom`, all rows in between `selected-middle`.
 */
export function getBatchSectionPositionClass(
  index: number,
  length: number,
): SelectionPositionClass | null {
  if (length <= 0 || index < 0 || index >= length) {
    return null;
  }
  if (length === 1) {
    return 'selected-single';
  }
  if (index === 0) {
    return 'selected-top';
  }
  if (index === length - 1) {
    return 'selected-bottom';
  }
  return 'selected-middle';
}

/**
 * Determines the selection position class for a bookmark based on adjacent selections.
 * @param bookmarkId - The ID of the current bookmark
 * @param index - The index of the bookmark in the list
 * @param bookmarks - The full array of bookmarks
 * @param selectedIds - Set of selected bookmark IDs
 * @returns The selection position class: 'selected-single', 'selected-top', 'selected-middle', 'selected-bottom', or null
 */
export function getSelectionPositionClass(
  bookmarkId: string,
  index: number,
  bookmarks: Bookmark[],
  selectedIds: Set<string>,
): SelectionPositionClass | null {
  // Normalize to string so we match selectedIds (which stores string ids)
  const idStr = String(bookmarkId);
  if (!selectedIds.has(idStr)) {
    return null;
  }

  // Check if previous and next bookmarks are selected
  const previousBookmark = index > 0 ? bookmarks[index - 1] : null;
  const nextBookmark = index < bookmarks.length - 1 ? bookmarks[index + 1] : null;

  const previousSelected = previousBookmark
    ? selectedIds.has(String(previousBookmark.id))
    : false;
  const nextSelected = nextBookmark ? selectedIds.has(String(nextBookmark.id)) : false;

  // Determine position class
  if (!previousSelected && !nextSelected) {
    // Standalone selected bookmark
    return 'selected-single';
  } else if (!previousSelected && nextSelected) {
    // First item in a selection block
    return 'selected-top';
  } else if (previousSelected && nextSelected) {
    // Middle item in a selection block
    return 'selected-middle';
  } else {
    // Last item in a selection block (previousSelected && !nextSelected)
    return 'selected-bottom';
  }
}
